import type { NewPlan } from "./schemas/plan.js";
import type { NewProgram } from "./schemas/program.js";
import type { Plan } from "./schemas/plan.js";
import type { Program, ProgramSession } from "./schemas/program.js";
import type {
  ResolvedPlanTemplate,
  ResolvedProgramTemplate,
} from "./templates.js";
import { mapAuthoredText, type AuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// Clone POST-body builders — the ONE place that knows how to turn an
// existing plan/program (a personal row OR a locale-resolved template)
// into a NewPlan / NewProgram payload. The id-stripping rules live here
// once so template-clone (TemplatesShelf, Library browser) and
// personal-clone can't drift:
//   - programs: session/slot ids are dropped — the server stamps fresh
//     UUIDs on POST so a clone never shares ids with its source (or
//     with another user's clone of the same template).
//   - plans: entries carry no ids (PlannedExerciseEntry is id-less),
//     so they pass through verbatim.
//   - a clone is always a personal draft: status 'draft' for programs
//     (the Follow+Publish lifecycle starts over), no assignees.

/** The subset of a plan a clone copies — both a personal `Plan` and a
 *  locale-`ResolvedPlanTemplate` satisfy it structurally. */
export type PlanCloneSource = Pick<Plan, "name" | "entries"> & {
  notes?: string;
};

/** The subset of a program a clone copies — both a personal `Program`
 *  and a `ResolvedProgramTemplate` satisfy it structurally. */
export type ProgramCloneSource = Pick<
  Program,
  "name" | "cycleWeeks" | "sessionsPerWeek"
> & {
  // `AuthoredText | string`, not `string`: a program's description may
  // carry several languages (PE-21), while a resolved template's is
  // already flattened to one — both have to satisfy this.
  description?: AuthoredText | string;
  // Carried so a clone keeps the language its text was WRITTEN in;
  // without it the copy would fall back through "any available" and
  // could read in a language its author never chose.
  authorLocale?: Locale;
  sessions: ReadonlyArray<ProgramSession>;
};

// Compile-time proof the resolved template shapes stay assignable to the
// clone sources (a template field rename breaks here, not at runtime).
const _planTplAssignable: PlanCloneSource = null as unknown as Omit<
  ResolvedPlanTemplate,
  "id" | "description" | "difficulty" | "tags"
>;
const _programTplAssignable: ProgramCloneSource = null as unknown as Omit<
  ResolvedProgramTemplate,
  "id" | "description" | "difficulty" | "tags"
>;
void _planTplAssignable;
void _programTplAssignable;

/** Plan.name / Program.name cap (schema max 120). A rename suffix must
 *  never push the clone body over it. */
const NAME_MAX = 120;

// Caps EVERY language present, keeping the value's shape: a clone of a
// translated program must arrive with the same languages as the
// original, each within the schema ceiling.
const capName = <T extends AuthoredText | string>(name: T): T =>
  mapAuthoredText(name, (s) => (s.length > NAME_MAX ? s.slice(0, NAME_MAX) : s));

export const planCloneBody = (
  src: PlanCloneSource,
  opts?: { name?: string },
): NewPlan => ({
  name: capName(opts?.name ?? src.name),
  entries: src.entries.map((e) => ({ ...e })),
  ...(src.notes ? { notes: src.notes } : {}),
});

export const programCloneBody = (
  src: ProgramCloneSource,
  // A clone of a translated program may be renamed in every language
  // it carries (see mapAuthoredText) — not collapsed to one.
  opts?: { name?: AuthoredText | string },
): NewProgram => ({
  name: capName(opts?.name ?? src.name),
  ...(src.description ? { description: src.description } : {}),
  ...(src.authorLocale ? { authorLocale: src.authorLocale } : {}),
  cycleWeeks: src.cycleWeeks,
  sessionsPerWeek: src.sessionsPerWeek,
  sessions: src.sessions.map((s) => ({
    name: s.name,
    ...(s.notes ? { notes: s.notes } : {}),
    slots: s.slots.map((slot) => {
      const { id: _slotId, ...rest } = slot;
      void _slotId;
      return rest;
    }),
  })),
  status: "draft",
});
